/**
 * Cookie utilities for managing user consent preferences
 */

const COOKIE_NAME = 'octopus_cookie_consent';
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Set a cookie with the given name, value, and expiry days
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiry days
 */
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Save cookie consent preferences
 * @param {Object} preferences - User preferences object
 */
export const setCookieConsent = (preferences) => {
  const consentData = {
    ...preferences,
    timestamp: new Date().toISOString(),
    version: '1.0'
  };
  setCookie(COOKIE_NAME, JSON.stringify(consentData), COOKIE_EXPIRY_DAYS);
};

/**
 * Get cookie consent preferences
 * @returns {Object|null} User preferences or null if not set
 */
export const getCookieConsent = () => {
  const consentCookie = getCookie(COOKIE_NAME);
  if (consentCookie) {
    try {
      return JSON.parse(consentCookie);
    } catch (error) {
      console.error('Error parsing cookie consent:', error);
      // If parsing fails, delete the corrupted cookie
      deleteCookie(COOKIE_NAME);
      return null;
    }
  }
  return null;
};

/**
 * Clear cookie consent preferences
 */
export const clearCookieConsent = () => {
  deleteCookie(COOKIE_NAME);
};

/**
 * Check if a specific cookie category is consented to
 * @param {string} category - Category name (analytics, marketing, functional)
 * @returns {boolean} Whether the category is consented to
 */
export const isCookieCategoryConsented = (category) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent[category] || false;
};
